/**
 * Descobre o DEPARTAMENTO de um cargo a partir da aba "Cargos e Salários".
 *
 * É o que decide se o colaborador vê o quadro de competências no cadastro: as
 * competências são todas de serviço de campo, então só aparecem para quem tem
 * CARGO do Operacional (Montador, Eletricista, Gestor Operacional…) — quem é do
 * Comercial, RH, DP, Qualidade… não tem essa parte no cadastro.
 *
 * A comparação é pelo cargo porque é ele que define a área da pessoa na tabela
 * do RH; o campo "Departamento" do cadastro é texto e pode estar em branco ou
 * divergente. O cadastro grava o cargo no formato "Cargo - Nível" (ver
 * CargoInput), então o índice guarda as duas formas.
 */
import type { Row } from "./types";
import { ehDoOperacional, normSkill as norm } from "./skills-catalogo";

/** cargo normalizado → departamento (da tabela de Cargos e Salários). */
export type IndiceCargos = Map<string, string>;

/** Monta o índice com as linhas de `/api/cargos`. */
export function indexarCargos(rows: Row[]): IndiceCargos {
  const indice: IndiceCargos = new Map();
  for (const r of rows) {
    const cargo = String(r.cargo ?? "").trim();
    const departamento = String(r.departamento ?? "").trim();
    if (!cargo || !departamento) continue;
    const nivel = String(r.nivel ?? "").trim();
    // "Montador - Pleno" (o que fica gravado no colaborador) e "Montador".
    if (nivel) indice.set(norm(`${cargo} - ${nivel}`), departamento);
    if (!indice.has(norm(cargo))) indice.set(norm(cargo), departamento);
  }
  return indice;
}

/** Departamento do cargo, ou null se o cargo não está na tabela do RH. */
export function departamentoDoCargo(
  cargo: unknown,
  indice: IndiceCargos | null | undefined,
): string | null {
  if (!indice) return null;
  const alvo = norm(cargo);
  if (!alvo) return null;
  const direto = indice.get(alvo);
  if (direto) return direto;
  // Cargo gravado com nível que não existe mais na tabela: tenta só o cargo.
  const semNivel = alvo.split(" - ")[0]?.trim();
  return (semNivel && indice.get(semNivel)) || null;
}

/**
 * O CARGO do colaborador é do Operacional?
 *
 * Falso também quando o cargo está em branco ou fora da tabela de Cargos e
 * Salários (cadastro antigo, digitado à mão) — nesse caso basta reabrir o
 * cadastro e selecionar o cargo no popup para o quadro voltar a aparecer.
 */
export function cargoEhDoOperacional(
  cargo: unknown,
  indice: IndiceCargos | null | undefined,
): boolean {
  return ehDoOperacional(departamentoDoCargo(cargo, indice));
}
